/**
 * Driver Home — /driver
 * Google-authenticated drivers see all their assigned trips + weekly earnings.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABEL = {
  clock_in: 'Clocked In', en_route: 'En Route', on_location: 'On Location',
  pob: 'POB', done: 'Complete', clock_out: 'Done',
};

export default function DriverHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [authState, setAuthState] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const me = await fetch(`${API}/api/auth/driver/me`, { credentials: 'include' });
      if (!me.ok) { setAuthState('denied'); return; }
      setAuthState('ok');
      const r = await fetch(`${API}/api/ops/driver/my-trips`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load trips');
      setData(await r.json());
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/driver';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (authState === 'denied') return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif', padding: 20 }}>
      <div style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', padding: 28, maxWidth: 380, width: '100%', textAlign: 'center' }} data-testid="driver-signin">
        <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: 0 }}>ACE · Driver Portal</p>
        <h1 style={{ fontSize: 20, fontWeight: 900, margin: '10px 0 6px' }}>My Trips</h1>
        <p style={{ fontSize: 12, color: '#525252', margin: '0 0 20px' }}>Sign in with the Google account on your driver profile to see your assigned trips, clock in, and track your weekly earnings.</p>
        <button onClick={signIn} style={{ width: '100%', padding: '14px', background: '#0A0A0A', color: '#FFFFFF', border: 0, fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }} data-testid="driver-google-signin">
          Sign in with Google
        </button>
        <p style={{ fontSize: 10, color: '#A3A3A3', marginTop: 14 }}>Not registered? Ask dispatch to add your email to your driver profile.</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif' }}>
      <p style={{ color: '#666', fontSize: 13 }} data-testid="driver-error">{error}</p>
    </div>
  );
  if (!data) return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif' }}>
      <p style={{ color: '#A3A3A3', fontSize: 13 }}>Loading your trips…</p>
    </div>
  );

  const { actor, upcoming, past, stats } = data;

  const TripCard = ({ t }) => (
    <button onClick={() => navigate(`/trip/${t.trip_token}`)} style={{ display: 'block', width: '100%', textAlign: 'left', background: '#FFFFFF', border: '1px solid #E5E5E5', padding: 14, marginBottom: 8, cursor: 'pointer' }} data-testid={`mytrip-${t.booking_id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 800, margin: 0, color: '#0A0A0A' }}>{t.client_name || 'Trip'}</p>
          <p style={{ fontSize: 11, color: '#525252', margin: '3px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.vehicle}{t.pickup_address ? ` · ${t.pickup_address}` : ''}{t.flight_number ? ` · ✈ ${t.flight_number}` : ''}
          </p>
          {t.driver_name && actor.role === 'admin' && <p style={{ fontSize: 10, color: '#A3A3A3', margin: '2px 0 0' }}>Driver: {t.driver_name}</p>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <p style={{ fontSize: 12, color: '#CC0000', fontWeight: 800, fontFamily: 'monospace', margin: 0 }}>{t.event_date}</p>
          <p style={{ fontSize: 10, color: '#525252', margin: '2px 0 0' }}>{t.pickup_time || ''}</p>
          {t.trip_status && <span style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: t.trip_status === 'clock_out' ? '#15803D' : '#B45309' }}>{STATUS_LABEL[t.trip_status] || t.trip_status}</span>}
        </div>
      </div>
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', color: '#0A0A0A', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', paddingBottom: 60 }} data-testid="driver-home">
      <Toaster position="top-center" theme="light" />
      <header style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E5E5', padding: '16px 20px' }}>
        <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: 0 }}>ACE · Driver Portal</p>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: '4px 0 0' }} data-testid="driver-name">{actor.name || actor.email}</h1>
        <p style={{ fontSize: 10, color: '#A3A3A3', margin: '2px 0 0' }}>{actor.role === 'admin' ? 'Admin view — all assigned trips' : 'Your assigned trips'}</p>
      </header>

      <main style={{ maxWidth: 480, margin: '0 auto', padding: 16 }}>
        {stats && (
          <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 20 }} data-testid="driver-stats">
            {[['This Week', `${stats.week_hours}h`], ['Trips', stats.week_trips], ['Earnings', `$${Number(stats.week_earnings).toLocaleString()}`]].map(([l, v]) => (
              <div key={l} style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', padding: '12px 10px', textAlign: 'center' }}>
                <p style={{ fontSize: 18, fontWeight: 900, margin: 0, fontFamily: 'monospace' }}>{v}</p>
                <p style={{ fontSize: 8, color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, margin: '2px 0 0' }}>{l}</p>
              </div>
            ))}
          </section>
        )}

        <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>Upcoming ({upcoming.length})</p>
        <section data-testid="driver-upcoming">
          {upcoming.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', background: '#FFFFFF', border: '1px solid #E5E5E5', fontSize: 12, color: '#A3A3A3', marginBottom: 20 }}>
              No upcoming trips assigned. Dispatch will assign you soon.
            </div>
          ) : upcoming.map(t => <TripCard key={t.booking_id} t={t} />)}
        </section>

        {past.length > 0 && (
          <>
            <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '20px 4px 10px' }}>Recent</p>
            <section data-testid="driver-past">
              {past.map(t => <TripCard key={t.booking_id} t={t} />)}
            </section>
          </>
        )}

        {actor.role === 'driver' && (
          <>
            <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '20px 4px 10px' }}>My Documents</p>
            <section style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', padding: 14 }} data-testid="driver-documents">
              <p style={{ fontSize: 11, color: '#525252', margin: '0 0 10px' }}>Keep your chauffeur license, insurance, and vehicle inspection photos on file with dispatch.</p>
              <AttachmentsSection entityType="driver" entityId={actor.driver_id || actor.email} title="License, Insurance & Inspections" uploadedBy={actor.name || actor.email} />
            </section>
          </>
        )}
      </main>
    </div>
  );
}
